import React from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { Switch } from 'components';
import {
  formatJson,
  HD_TICKET_FORMAT,
  HD_TICKET_FORMAT_STR,
} from 'utils/common';
import { useTranslation } from 'react-i18next';
import { TextArea } from 'components/TextArea';
import { useDealerRole } from 'hooks/role';
import { IDealerConfigProps } from 'thunks/RTKQuery/dealer';

interface IConfigComponentProps {
  handleSubmit: () => void;
  isError: boolean;
  errorMsg: string;
  data: any;
  handleUpdateProductionConfig: (configJson: string) => void;
  dealerConfig: IDealerConfigProps;
  setDealerConfig: (config: IDealerConfigProps) => void;
}

interface IFormValidation {
  isValidJson: boolean;
  jsonValidationMessage: string;
}

export const ConfigComponent: React.FC<IConfigComponentProps> = ({
  isError,
  errorMsg,
  data,
  dealerConfig,
  setDealerConfig,
  handleSubmit,
  handleUpdateProductionConfig,
}: IConfigComponentProps) => {
  const { t } = useTranslation();
  const isDealerUser = useDealerRole();

  const [validationResult, setValidationResult] = React.useState<
    IFormValidation
  >({
    isValidJson: false,
    jsonValidationMessage: '',
  });
  const [configResponse, setConfigResponse] = React.useState<string>('');
  const [invalidTicketFormat, setInvalidTicketFormat] = React.useState<boolean>(
    false
  );

  React.useEffect(() => {
    if (!data) return;
    try {
      setConfigResponse(JSON.stringify(data, null, 2));
    } catch (error) {
      setConfigResponse((error as any)?.message || error);
    }
  }, [data]);

  const validateJsonConfig = () => {
    let isValidJsonConfig = false;
    const configJson = dealerConfig.isTestMode
      ? dealerConfig.draftConfiguration
      : dealerConfig.prodConfiguration;

    try {
      JSON.parse(configJson);
      setValidationResult({
        isValidJson: true,
        jsonValidationMessage: t('dealerManagement.dealerConfig.validJson'),
      });
      isValidJsonConfig = true;
    } catch (e) {
      setValidationResult({
        isValidJson: false,
        jsonValidationMessage: t('dealerManagement.dealerConfig.invalidJson'),
      });
    }

    /* Only pretty-format the configuration we just validated and only
       when validation succeeded */
    if (isValidJsonConfig) {
      const formatted = formatJson(configJson);
      setDealerConfig({
        ...dealerConfig,
        ...(dealerConfig.isTestMode
          ? { draftConfiguration: formatted }
          : { prodConfiguration: formatted }),
      });
    }
    return isValidJsonConfig;
  };
  const onSubmit = async () => {
    if (!HD_TICKET_FORMAT.test(dealerConfig.draftTicketId)) {
      setInvalidTicketFormat(true);
      return;
    }
    if (!validateJsonConfig()) {
      // JSON validation failed
      return;
    }
    // Call the handleSubmit function
    handleSubmit();
  };

  return (
    <>
      <Row className="mb-30">
        <Col>
          <h2>{t('dealerManagement.dealerConfig.test')}</h2>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <div className="text-danger">
            {isError ? errorMsg : ''}
            {invalidTicketFormat
              ? t('dealerManagement.dealerConfig.invalidTicketFormat', {
                  HD_TICKET_FORMAT_STR,
                })
              : ''}
          </div>
          {!validationResult.isValidJson &&
            validationResult.jsonValidationMessage.length > 0 && (
              <div className="text-danger">
                {validationResult.jsonValidationMessage}
              </div>
            )}
          {validationResult.isValidJson &&
            validationResult.jsonValidationMessage.length > 0 && (
              <div className="text-success">
                {validationResult.jsonValidationMessage}
              </div>
            )}
        </Col>
      </Row>

      <Row>
        <Col md={10}>
          <small className="fs-10" style={{ color: 'grey' }}>
            {t('dealerManagement.dealerConfig.ticketFormat')}:
            XXXXX-XXXXX-XXXXX-XXXXX-XXXXX
          </small>
        </Col>
        <Col md={2} />
      </Row>
      <Row className="mb-30 d-flex align-items-center">
        <Col md={6}>
          <input
            type="text"
            className="form-control border"
            value={dealerConfig.draftTicketId}
            onChange={(e) => {
              setDealerConfig({
                ...dealerConfig,
                draftTicketId: e.target.value,
              });
              setInvalidTicketFormat(false);
            }}
          />
        </Col>

        <Col md={6}>
          {isDealerUser && dealerConfig.isTestMode && (
            <Button
              className="w-100 btn-info"
              onClick={() => {
                if (!validateJsonConfig()) {
                  // JSON validation failed
                  return;
                }
                handleUpdateProductionConfig(dealerConfig.draftConfiguration);
              }}
            >
              {t('dealerManagement.dealerConfig.readyForProduction')}
            </Button>
          )}
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Row>
            <Col md={6}>
              <Switch
                className="d-flex align-items-center"
                label={t('dealerManagement.dealerConfig.initialCall')}
                id="isInitCall"
                checked={dealerConfig.isInitialCall}
                onChange={(e) =>
                  setDealerConfig({
                    ...dealerConfig,
                    isInitialCall: e.target.checked,
                  })
                }
              />
            </Col>
            <Col
              md={6}
              className="d-flex align-items-center justify-content-end"
            >
              <Switch
                className="d-flex align-items-center"
                label={t('dealerManagement.dealerConfig.testMode')}
                id="isTestMode"
                checked={dealerConfig.isTestMode}
                onChange={(e) =>
                  setDealerConfig({
                    ...dealerConfig,
                    isTestMode: e.target.checked,
                  })
                }
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className="mb-10">
        <Col md={6}>
          <small className="fs-10" style={{ color: 'grey' }}>
            {t('dealerManagement.dealerConfig.configJson')}
          </small>
          <TextArea
            customClasses="border"
            name="configJson"
            value={
              dealerConfig.isTestMode
                ? dealerConfig.draftConfiguration
                : dealerConfig.prodConfiguration
            }
            rows={20}
            resize={false}
            onChange={(e: any) => {
              if (dealerConfig.isTestMode) {
                setDealerConfig({
                  ...dealerConfig,
                  draftConfiguration: e.target.value,
                });
                return;
              }
              // in case of production config, update prodConfiguration
              setDealerConfig({
                ...dealerConfig,
                prodConfiguration: e.target.value,
              });
            }}
          />

          <Row className="mt-10">
            <Col md={12}>
              <Button
                className="w-100 btn-outline-primary"
                onClick={validateJsonConfig}
              >
                {t('dealerManagement.dealerConfig.validateJson')}
              </Button>
            </Col>
          </Row>
        </Col>
        <Col md={6}>
          <small className="fs-10" style={{ color: 'grey' }}>
            {t('dealerManagement.dealerConfig.response')}
          </small>
          <TextArea
            customClasses="border"
            name="configJsonResult"
            value={configResponse}
            rows={20}
            disabled={false}
            resize={false}
          />
          <Row className="mt-10">
            <Col md={12}>
              <Button onClick={onSubmit} className="w-100">
                {t('dealerManagement.dealerConfig.viewResult')}
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default ConfigComponent;
